import { AvcResult } from '@vfde-sails/avc-core';
import { PreMarketingType, ProviderType } from '../enums/product';
import { AdditionalPageOptions, BenefitsData } from '../interfaces/additionalPageOptions';
import { formatEuro, formatPrice } from './formatter';

/**
 * Get CMS config from additionalPageOptions
 */
export const getCMSConfig = () => window.additionalPageOptions as AdditionalPageOptions;

/**
 * Get Fiber object from window
 */
export const getFiberObject = () => window.fiber;

/**
  * Get SAILS object property for fiber repo by 'key'
*/
export const getFiberObjectProperty = (key: string) => getFiberObject()?.[key];

/**
 * Set SAILS object property for fiber repo by 'key' and 'value'
 */
export const setFiberProperty = (key: string, value: any) => {
    if (!window.fiber) {
        window.fiber = {};
    }

    window.fiber[key] = value;
};

/**
 * Returns tariffIds in an array for the provided productId
 *
 * @param providerType ProviderType
 * @returns string[]
 */
export const getTariffIds = (providerType: ProviderType): string[] => {
    return Object.values(getCMSConfig()?.tariffIds?.[providerType] || {});
};

/**
 * Returns data for the tariff benefits list
 *
 * @param tariffId string
 * @param avcResult result of the avc check
 * @returns BenefitsData[]
 */
export const getBenefitsData = (tariffId: string, avcResult?: AvcResult) => {
    const config = getCMSConfig();
    if (!config) {
        return;
    }

    const benefits = Object.values(config.tariffBenefits || {});
    const availableBenefits = benefits.filter(benefit => Object.values(benefit.tariffs).includes(tariffId));
    const regioFee = avcResult?.selectedAvailable
        ? avcResult.selected.regioCharge
        : avcResult?.fastest?.regioCharge;

    if (regioFee && config.regioFeeBenefit) {
        const regioFeeBenefit: BenefitsData = {
            ...config.regioFeeBenefit,
            headline: formatEuro(formatPrice(config.regioFeeBenefit.headline, regioFee)),
        };
        availableBenefits.push(regioFeeBenefit);
    }

    return availableBenefits;
};

export const getPreMarketingAvcResult = (preMarketingType?: PreMarketingType) => {
    const config = getCMSConfig()?.avcResult;

    if (!config) {
        return undefined;
    }

    switch (preMarketingType) {
        case PreMarketingType.BHS:
            return config.homesPassedBHS;
        case PreMarketingType.BKV:
            return config.homesPassedBKV;
        case PreMarketingType.PRE_FIBER:
            return config.preFiber;
    }
};
